import { Amount, Coverage, Term } from '@sky-distribution/adapter-core-pc-rest-interfaces';
import {
  Term as DigitalTerm,
  Coverage as GPCoverage, Variants,
} from '@sky-distribution/digital-gp-submission';
import { GenericVariant } from '@sky-distribution/gp-onboarding-policy-service';
import { OfferDetailItem } from '@sky-distribution/onboarding-commons-ui/lib/ItemDetailsModal/ItemDetailsModal.types';
import {
  CoverageMeta,
  CoverageMetaList,
  CoveragesDetails,
  CoverageType,
  CoverageWithMeta,
  CoverageWithMetaMap,
  DigitalCoverage,
  DigitalCoverages,
  WithRequired,
} from '../Coverage.type';
import { SubmissionOptions } from '../GenericSubmission/GenericSubmission.type';

export const defaultCoverageMeta: CoverageMeta = {
  isHidden: false,
};

const isCoverageWithCode = (
  coverage: Coverage,
): coverage is WithRequired<Coverage, 'codeIdentifier'> => typeof coverage.codeIdentifier === 'string';

export const getCoverageMeta = (
  coverageCode: string,
  coveragesMeta: CoverageMetaList,
): CoverageMeta => {
  const meta = coveragesMeta[coverageCode];

  return meta || defaultCoverageMeta;
};

const isSomeOfGroupedCoveragesSelected = (
  group: DigitalCoverage,
  coverageMap: CoverageWithMetaMap,
): boolean => !!group.triggeredBy?.some(
  (code) => !!coverageMap.get(code)?.coverage.selected,
);

const getCoverageTerms = (group: DigitalCoverage,
  coverageMap: CoverageWithMetaMap): Term[] | undefined => {
  if (group.triggeredBy) {
    const coverage = coverageMap.get(group.triggeredBy[0]);

    return coverage?.coverage.terms;
  }

  return undefined;
};

const populateMap = (
  coverages: Coverage[],
  map: CoverageWithMetaMap,
  type: CoverageType,
  coveragesMeta: CoverageMetaList,
): void => {
  coverages.forEach((coverage) => {
    if (isCoverageWithCode(coverage)) {
      map.set(coverage.codeIdentifier, {
        coverage,
        type,
        codeIdentifier: coverage.codeIdentifier,
        ...getCoverageMeta(coverage.codeIdentifier, coveragesMeta),
      });
    }
  });
};

export const createCoverageMap = (coverages?: CoverageWithMeta[]) => {
  if (!coverages) {
    return new Map<string, CoverageWithMeta>();
  }

  return coverages.reduce(
    (acc, coverage) => {
      acc.set(coverage.codeIdentifier, coverage);

      return acc;
    }, new Map<string, CoverageWithMeta>(),
  );
};

export const populateWithMeta = ({
  mainCoverages,
  additionalCoverages = [],
  digitalCoverages,
  coveragesMeta,
  coveragesDetails,
}: {
  mainCoverages: Coverage[];
  additionalCoverages?: Coverage[];
  digitalCoverages: DigitalCoverages;
  coveragesMeta: CoverageMetaList;
  coveragesDetails?: CoveragesDetails;
}): CoverageWithMeta[] => {
  const coverageMap = createCoverageMap();

  populateMap(mainCoverages, coverageMap, CoverageType.main, coveragesMeta);

  populateMap(
    additionalCoverages,
    coverageMap,
    CoverageType.additional,
    coveragesMeta,
  );

  const coveragesWithGroups = [...mainCoverages, ...additionalCoverages];

  const coveragesWithMeta: CoverageWithMeta[] = coveragesWithGroups
    .filter(isCoverageWithCode)
    .map((coverage) => ({
      coverage,
      type: coverageMap.get(coverage.codeIdentifier)?.type,
      codeIdentifier: coverage.codeIdentifier,
      name: (coveragesDetails?.[coverage.codeIdentifier] as OfferDetailItem)?.name,
      ...getCoverageMeta(coverage.codeIdentifier, coveragesMeta),
    }));

  Object.keys(digitalCoverages)
    .forEach((groupCode) => {
      const group = digitalCoverages[groupCode];
      const terms = getCoverageTerms(group, coverageMap);
      const isSelected = isSomeOfGroupedCoveragesSelected(group, coverageMap);

      group.coverage.terms = terms;
      group.coverage.selected = isSelected;

      coveragesWithMeta.push(group);
    });

  return coveragesWithMeta;
};

export const getMainCoverages = (
  variant: GenericVariant | undefined,
  propertyKey: string,
  coverageKey: string,
): Coverage[] => {
  const propertyVariant = variant?.[propertyKey];

  return propertyVariant?.[coverageKey] ?? [];
};

export const generateCoveragesWithMeta = (
  variants: GenericVariant[],
  digitalCoverages: DigitalCoverages,
  coveragesMeta: CoverageMetaList,
  variantId: number,
  options: SubmissionOptions,
  coveragesDetails?: CoveragesDetails,
): CoverageWithMeta[] => {
  const mainCoverages = getMainCoverages(
    variants[variantId],
    options.propertyKey,
    options.propertyCoveragesKey,
  );
  const additionalCoverages = variants[variantId]?.coverages;

  return populateWithMeta({
    mainCoverages,
    additionalCoverages,
    digitalCoverages,
    coveragesMeta,
    coveragesDetails,
  });
};

const mapToLegacyTerm = (
  term: DigitalTerm,
): Term => ({ chosenTermValue: term.chosenTermValue, patternCode: term.code });

const mapToLegacyCoverages = (
  coverages: GPCoverage[],
): Coverage[] => coverages.map(({
  coverageCode, amount, name, description, required, selected, terms,
}) => ({
  codeIdentifier: coverageCode,
  amount: amount as Amount | undefined,
  name,
  description,
  required,
  selected,
  terms: terms?.map(mapToLegacyTerm),
}));

export const mapToLegacyVariants = (
  variants: Variants | undefined,
): GenericVariant[] | undefined => (
  Array.isArray(variants)
    ? variants.map(({ name, coverages }) => ({
      offeringCode: name,
      coverages: coverages && mapToLegacyCoverages(coverages),
    }))
    : undefined
);
